import { nanoid } from 'nanoid';
import KatalLogger, { Level, LoggerConfig } from '@amzn/katal-logger';
import { Metric, ReportHandler } from 'web-vitals';

/**
 * Enum that has valid CloudWatch Units which we use in our app
 * More info: https://docs.aws.amazon.com/AmazonCloudWatch/latest/APIReference/API_MetricDatum.html
 */
export enum METRIC_UNIT {
  COUNT = 'Count',
  MILLISECONDS = 'Milliseconds',
  NONE = 'None'
}

export enum METRIC_NAME {
  APP_MOUNT = 'app.react.mount',
  APP_LATENCY_CF = 'app.latency.cf',
  APP_ERROR_UNKNOWN = 'app.error.unknown'
}

export enum DIMENSION_NAME {
  PAGE_NAME = 'PageName',
  DURATION = 'duration'
}

export type DIMENSION_VALUES = {
  [key in DIMENSION_NAME]?: any;
};

export type EMFMetric = {
  _aws: {
    Timestamp: number;
    CloudWatchMetrics: {
      Namespace: string;
      Metrics: { Name: string; Unit?: string }[];
    }[];
  };
};

export type MetaDataMetric = {
  metricName: METRIC_NAME | Metric['name'];
  metricValue: number;
  metricUnit: METRIC_UNIT;
  dimensions?: DIMENSION_VALUES;
  namespace?: string;
};

export const atozClientRequestId = nanoid();

/**
 * To publish logs to a personal API-GW, deploy the stack in the AtoZMyResultsAppLoggingCDK package
 * to your personal AWS account and change the configuration for "default" below.
 */
const API_GW_URL_BY_STAGE: Record<string, string> = {
  'Prod': 'https://6rt4hw6gs2.execute-api.us-west-2.amazonaws.com/prod/v1/log',
  'Gamma': 'https://4zkoqd0a4i.execute-api.us-west-2.amazonaws.com/prod/v1/log',
  'Beta': 'https://9tcghv0jcl.execute-api.us-west-2.amazonaws.com/prod/v1/log',
  'default': 'https://9tcghv0jcl.execute-api.us-west-2.amazonaws.com/prod/v1/log',
}

const loggerConfig: LoggerConfig = {
  url: API_GW_URL_BY_STAGE['{{Stage}}'] || API_GW_URL_BY_STAGE['default'],
  logThreshold: Level.INFO,
  maxLogLineSize: 10001,
  logToConsole: true,
  context: { 'x-atoz-client-request-id': atozClientRequestId }
};

class MyResultsLogger extends KatalLogger {
  constructor(config: LoggerConfig) {
    super(config);
  }

  /**
   * Given metric/dimension values, creates a EMF payload in the expected
   * AWS syntax.
   *
   * More Info: https://docs.aws.amazon.com/AmazonCloudWatch/latest/monitoring/CloudWatch_Embedded_Metric_Format_Specification.html
   */
  private createEmfLog = ({
    metricName,
    metricValue,
    metricUnit,
    dimensions,
    namespace = 'AtoZMyResultsApp'
  }: MetaDataMetric): EMFMetric => {
    // Add the page name dimensions to all incoming dimensions
    const allDimensions: DIMENSION_VALUES = {
      PageName: 'Default',
      ...dimensions
    };
    const emfLog = {
      _aws: {
        Timestamp: Date.now(),
        CloudWatchMetrics: [
          {
            Namespace: namespace,
            Dimensions: Object.keys(allDimensions).map((d) => [d]),
            Metrics: [
              {
                Name: metricName,
                Unit: metricUnit
              }
            ]
          }
        ]
      },
      ...allDimensions,
      [metricName]: metricValue
    };

    return emfLog;
  };

  /**
   * Reports individual web-vitals
   */
  private reportWebVital: ReportHandler = ({ name, value }: Metric) => {
    this.logEmfMetric({
      metricName: name,
      metricValue: value,
      metricUnit: name !== 'CLS' ? METRIC_UNIT.MILLISECONDS : METRIC_UNIT.NONE
    });
  };

  /**
   * Reports all the web vitals
   * https://github.com/GoogleChrome/web-vitals
   */
  public reportWebVitals = async () => {
    const { getFID, getFCP, getLCP, getTTFB } = await import('web-vitals');
    getFID(this.reportWebVital);
    getFCP(this.reportWebVital);
    getLCP(this.reportWebVital);
    getTTFB(this.reportWebVital);
  };

  /**
   * Logs an EMF metric using the Katal logger (with EMF payload added to the log)
   */
  public logEmfMetric = (metricPayload: MetaDataMetric): void => {
    const emfLog = this.createEmfLog(metricPayload);
    void this.info(metricPayload.metricName, { emfLog: emfLog });
  };

  /**
   * Helper method to log count metric with count = 1
   */
  public emitCountMetric = ({
    metricName,
    dimensions
  }: Omit<MetaDataMetric, 'metricValue' | 'metricUnit'>): void => {
    this.logEmfMetric({
      metricName: metricName,
      metricValue: 1,
      metricUnit: METRIC_UNIT.COUNT,
      dimensions
    });
  };

  /**
   * Helper method to log latency metric (in milliseconds)
   */
  public emitLatencyMetric = ({
    metricName,
    metricValue,
    dimensions
  }: Omit<MetaDataMetric, 'metricUnit'>): void => {
    this.logEmfMetric({
      metricName: metricName,
      metricValue: metricValue,
      metricUnit: METRIC_UNIT.MILLISECONDS,
      dimensions
    });
  };
}

export const logger = new MyResultsLogger(loggerConfig);
